'use client'

import { ButtonPrimary } from '@fukku/button/Primary'
import { ButtonSecondary } from '@fukku/button/Secondary'
import { Modal as FukkuModal } from '@fukku/modal'
import { MODAL_TYPE, type ModalAPI } from '@fukku/modal/types'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useProductListAnalyticsProvider } from 'product-list/context/ProductListAnalyticsProvider/useProductListAnalyticsProvider'
import {
	FilterAction,
	type FiltersChildProps,
} from 'product-list/types/Filters'
import { dataTestIds } from 'product-list/utils/testIds'
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'

import { useProductListFiltersProvider } from '../../../context/ProductListFiltersProvider/useProductListFiltersProvider'
import { FiltersAccordions } from './FiltersAccordions/FiltersAccordions'

import { scrollToProductListHook } from '../../ProductListHook/ProductListHook'
import styles from './Modal.module.scss'

export function Modal({
	show,
	onClose,
}: Readonly<FiltersChildProps>): ReactNode {
	const modalRef = useRef<ModalAPI>(null)

	const { t } = useLabels()

	const {
		nAvailableFilters,
		selectedFilters,
		selectedRange,
		selectedOrder,
		selectedLimitRange,
		selectedCount,
		hasSelection,
		clear,
		apply,
	} = useProductListFiltersProvider()

	const {
		onFiltersWasApplied,
		onFiltersWasClosed,
		onFiltersWasCleaner,
		onZeroResults,
	} = useProductListAnalyticsProvider()

	const handleClick = useCallback(
		(action: FilterAction) => {
			const actions: Record<FilterAction, () => undefined> = {
				[FilterAction.apply]: () => {
					apply()
					onFiltersWasApplied?.({
						selectedCount,
						selectedFilters,
						selectedOrder,
						selectedRange,
						selectedLimitRange,
					})
					modalRef.current?.close()
					onClose?.()
					scrollToProductListHook({ isSmooth: true })
				},
				[FilterAction.clear]: () => {
					clear()
					onFiltersWasCleaner?.()
				},
				[FilterAction.close]: () => {
					onClose?.()
					onFiltersWasClosed?.()
				},
			}

			return actions[action]?.()
		},
		[
			apply,
			clear,
			onClose,
			onFiltersWasApplied,
			onFiltersWasCleaner,
			onFiltersWasClosed,
			selectedCount,
			selectedFilters,
			selectedLimitRange,
			selectedOrder,
			selectedRange,
		]
	)

	useEffect(() => {
		if (show && nAvailableFilters > 0 && !modalRef.current?.isOpen) {
			modalRef.current?.show()
		}
	}, [nAvailableFilters, show])

	useEffect(() => {
		if (show && selectedCount === 0) {
			onZeroResults?.()
		}
	}, [onZeroResults, selectedCount, show])

	const subtitle = useMemo(() => {
		if (hasSelection) {
			if (selectedCount === 0) {
				return t('product.filters.noResults.text')
			}
			if (selectedCount === 1) {
				return t('product.filters.results.one.text')
			}
			if (selectedCount > 1) {
				return replacePlaceholders(t('product.filters.results.more.text'), {
					1: selectedCount.toString(),
				})
			}
		}
		return ''
	}, [hasSelection, selectedCount, t])

	const handleClearClick = useCallback(
		() => handleClick(FilterAction.clear),
		[handleClick]
	)
	const handleApplyClick = useCallback(
		() => handleClick(FilterAction.apply),
		[handleClick]
	)
	const handleCloseClick = useCallback(
		() => handleClick(FilterAction.close),
		[handleClick]
	)

	if (!show) {
		return null
	}

	return (
		<FukkuModal
			name='modal-filters'
			ref={modalRef}
			className={styles.modal}
			type={MODAL_TYPE.SIDE}
			data-testid={dataTestIds.plpFilterPanel(true)}
			onClose={handleCloseClick}
			title={t('buscador.filtrar')}
			subtitle={subtitle}
			footer={
				<div className={styles.footer}>
					<ButtonSecondary
						disabled={!hasSelection}
						onClick={handleClearClick}
						data-testid={dataTestIds.plpFilterClearButton}
					>
						{t('product.filters.resetFilters.button')}
					</ButtonSecondary>
					<ButtonPrimary
						disabled={selectedCount === 0}
						onClick={handleApplyClick}
						data-testid={dataTestIds.plpFilterApplyButton}
					>
						{t('product.filters.viewResult.button')}
					</ButtonPrimary>
				</div>
			}
			showTitle
		>
			<FiltersAccordions />
		</FukkuModal>
	)
}
